"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import {
    X,
    Plus,
    MoreVertical,
    Copy,
    Trash2,
    Play,
    Pencil,
    Type,
    ImageIcon as ImgIcon,
} from "lucide-react";

import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    WalkthroughBlock,
    WalkthroughStep,
    WalkthroughStepType,
} from "@/lib/utils/lomdaV3/types";
import { createWalkthroughStep } from "../../../lib/blockRegistry";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useLomdaV3Theme } from "../../../hooks/useLomdaV3Theme";
import InfoStepEditor from "./infoStepEditor";
import ImageStepEditor from "./imageStepEditor";
import WalkthroughPlayer from "./walkthroughPlayer";

function cloneStep(s: WalkthroughStep): WalkthroughStep {
    if (s.type === "image") {
        return {
            ...s,
            id: uuidv4(),
            hotspots: s.hotspots.map((h) => ({ ...h, id: uuidv4() })),
        };
    }
    return {
        ...s,
        id: uuidv4(),
        buttons: s.buttons.map((b) => ({ ...b, id: uuidv4() })),
    };
}

export default function WalkthroughEditor({
    block,
    onClose,
}: {
    block: WalkthroughBlock;
    onClose: () => void;
}) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const theme = useLomdaV3Theme();
    const steps = block.data.steps;
    const [selectedId, setSelectedId] = useState(steps[0]?.id ?? "");
    const [preview, setPreview] = useState(false);

    const setSteps = (next: WalkthroughStep[]) =>
        updateBlock(block.id, { steps: next });

    const addStep = (type: WalkthroughStepType) => {
        const step = createWalkthroughStep(type);
        setSteps([...steps, step]);
        setSelectedId(step.id);
    };

    const updateStep = (id: string, patch: Partial<WalkthroughStep>) =>
        patchBlockData(block.id, "walkthrough", (d) => ({
            steps: d.steps.map((s) =>
                s.id === id ? ({ ...s, ...patch } as WalkthroughStep) : s
            ),
        }));

    const deleteStep = (id: string) => {
        const next = steps.filter((s) => s.id !== id);
        setSteps(next);
        if (selectedId === id) setSelectedId(next[0]?.id ?? "");
    };

    const duplicateStep = (id: string) => {
        const index = steps.findIndex((s) => s.id === id);
        if (index === -1) return;
        const copy = cloneStep(steps[index]);
        setSteps([
            ...steps.slice(0, index + 1),
            copy,
            ...steps.slice(index + 1),
        ]);
        setSelectedId(copy.id);
    };

    const selected = steps.find((s) => s.id === selectedId) ?? null;

    // Portalled to the body: the canvas is a size container, which makes it the
    // containing block for `fixed` descendants — rendered in place, this
    // full-screen editor would be trapped inside the canvas instead of
    // covering the window.
    if (typeof document === "undefined") return null;

    return createPortal(
        // The theme travels with it: the lomda's shadcn tokens live on the
        // canvas, not on <body>, so a portalled child would silently fall back
        // to the platform palette and change colour.
        <div className="fixed inset-0 z-50" dir="ltr" style={theme}>
            <div className="lomda-v3-card-surface flex h-full flex-col">
                <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
                    <span className="lomda-v3-text-label">
                        {t("lomda-v3-editor.walkthrough.editor-title")}
                    </span>
                    <div className="flex items-center gap-2">
                        {steps.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setPreview((p) => !p)}
                                className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 lomda-v3-text-small text-muted-foreground transition hover:text-foreground"
                            >
                                {preview ? (
                                    <Pencil className="size-4" />
                                ) : (
                                    <Play className="size-4" />
                                )}
                                {preview
                                    ? t("lomda-v3-editor.walkthrough.edit")
                                    : t("lomda-v3-editor.walkthrough.preview")}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                        >
                            <X className="size-5" />
                        </button>
                    </div>
                </div>

                {preview ? (
                    <div className="min-h-0 flex-1">
                        <WalkthroughPlayer steps={steps} className="h-full" />
                    </div>
                ) : steps.length === 0 ? (
                    <StepTypePicker onPick={addStep} />
                ) : (
                    <div className="flex min-h-0 flex-1">
                        <aside className="flex w-64 shrink-0 flex-col border-e bg-muted/20">
                            <div className="p-3">
                                <NewStepButton onPick={addStep} />
                            </div>
                            <div className="flex flex-col gap-2 overflow-y-auto px-3 pb-3">
                                {steps.map((s, i) => (
                                    <StepCard
                                        key={s.id}
                                        step={s}
                                        index={i}
                                        active={s.id === selectedId}
                                        onSelect={() => setSelectedId(s.id)}
                                        onDuplicate={() => duplicateStep(s.id)}
                                        onDelete={() => deleteStep(s.id)}
                                    />
                                ))}
                            </div>
                        </aside>

                        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/10 p-6">
                            {selected && (
                                <div className="mx-auto max-w-4xl">
                                    <div className="mb-4 flex items-center gap-2">
                                        <span className="lomda-v3-text-small text-muted-foreground">
                                            {t("lomda-v3-editor.walkthrough.step-name")}
                                        </span>
                                        <Input
                                            value={selected.name}
                                            onChange={(e) =>
                                                updateStep(selected.id, { name: e.target.value })
                                            }
                                            placeholder={t("lomda-v3-editor.walkthrough.step-name-placeholder")}
                                            className="lomda-v3-page-text h-8 max-w-xs"
                                        />
                                    </div>
                                    {selected.type === "info" ? (
                                        <InfoStepEditor
                                            step={selected}
                                            steps={steps}
                                            onUpdate={(p) => updateStep(selected.id, p)}
                                        />
                                    ) : (
                                        <ImageStepEditor
                                            step={selected}
                                            steps={steps}
                                            onUpdate={(p) => updateStep(selected.id, p)}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

function StepTypePicker({
    onPick,
}: {
    onPick: (type: WalkthroughStepType) => void;
}) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-8 p-6">
            <h2 className="lomda-v3-text-h5">
                {t("lomda-v3-editor.walkthrough.start-with")}
            </h2>
            <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
                <TypeCard
                    icon={<Type className="size-6" />}
                    title={t("lomda-v3-editor.walkthrough.info-step")}
                    desc={t("lomda-v3-editor.walkthrough.info-step-desc")}
                    onClick={() => onPick("info")}
                />
                <TypeCard
                    icon={<ImgIcon className="size-6" />}
                    title={t("lomda-v3-editor.walkthrough.image-step")}
                    desc={t("lomda-v3-editor.walkthrough.image-step-desc")}
                    onClick={() => onPick("image")}
                />
            </div>
        </div>
    );
}

function TypeCard({
    icon,
    title,
    desc,
    onClick,
}: {
    icon: React.ReactNode;
    title: string;
    desc: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex flex-col gap-2 rounded-xl border bg-card p-5 text-start transition hover:border-primary hover:shadow-sm"
        >
            <span className="flex size-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                {icon}
            </span>
            <span className="lomda-v3-text-label">{title}</span>
            <span className="lomda-v3-text-small text-muted-foreground">{desc}</span>
        </button>
    );
}

function NewStepButton({
    onPick,
}: {
    onPick: (type: WalkthroughStepType) => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="flex w-full items-center justify-center gap-1.5 rounded-full bg-primary py-2 lomda-v3-text-label text-primary-foreground transition hover:opacity-90"
                >
                    <Plus className="size-4" />
                    {t("lomda-v3-editor.walkthrough.new-step")}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onSelect={() => onPick("info")}>
                    <Type className="size-4 me-2" />
                    {t("lomda-v3-editor.walkthrough.info-step")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onPick("image")}>
                    <ImgIcon className="size-4 me-2" />
                    {t("lomda-v3-editor.walkthrough.image-step")}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function StepCard({
    step,
    index,
    active,
    onSelect,
    onDuplicate,
    onDelete,
}: {
    step: WalkthroughStep;
    index: number;
    active: boolean;
    onSelect: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
}) {
    return (
        <div
            className={cn(
                "flex items-center gap-2 rounded-lg border bg-card p-2 transition",
                active ? "border-primary ring-1 ring-primary" : "hover:border-muted-foreground/40"
            )}
        >
            <button
                type="button"
                onClick={onSelect}
                className="flex min-w-0 flex-1 items-center gap-2 text-start"
            >
                <span className="flex size-6 shrink-0 items-center justify-center rounded bg-muted lomda-v3-text-small">
                    {index + 1}
                </span>
                {step.type === "image" ? (
                    <ImgIcon className="size-4 shrink-0 text-muted-foreground" />
                ) : (
                    <Type className="size-4 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate lomda-v3-text-small">
                    {step.name ||
                        t("lomda-v3-editor.walkthrough.step-n", undefined, {
                            n: index + 1,
                        })}
                </span>
            </button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:bg-muted"
                    >
                        <MoreVertical className="size-3.5" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={onDuplicate}>
                        <Copy className="size-4 me-2" />
                        {t("lomda-v3-editor.block.duplicate")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onSelect={onDelete}
                        className="text-destructive focus:text-destructive"
                    >
                        <Trash2 className="size-4 me-2" />
                        {t("lomda-v3-editor.block.delete")}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
